import { useState, useEffect } from 'react';

import { Layout } from 'components/layout/Layout';
import Location from 'services/Location';
import { getProductName } from 'helpers/product';
import { Product } from 'types/product';
import { parseJwt } from 'helpers/token';
import {
  generateAvatarHeroText,
  getShouldDisplayExternalChooser,
} from 'routes/chooser/chooser-layout/helpers';
import { getAccessToken } from 'helpers/account';
import { useProducts } from 'hooks/use-products';
import { useGetMe } from 'hooks/use-get-me';
import { fetchClient } from 'hooks/use-get-client';
import { OrganizationGraphQLResponse } from 'hooks/use-organizations';

import { AvatarHero } from './avatar-hero/AvatarHero';

import * as styles from './styles';

type Props = {
  isOnActivate?: boolean;
  isAccountWhitelisted?: boolean;
  organizations: OrganizationGraphQLResponse[];
  children: React.ReactNode;
};

export const ChooserLayout: React.FC<Props> = ({
  children,
  organizations,
  isAccountWhitelisted,
  isOnActivate = false,
}) => {
  const [isOnExternalChooser, setIsOnExternalChooser] = useState(false);
  const [appName, setAppName] = useState('');

  const { avatarUrl, email, name, roles } = useGetMe();
  const { isFetchingProducts, products } = useProducts();

  useEffect(() => {
    const isTextAppOnlySingleOrg =
      organizations.length === 1 &&
      organizations[0].products.length === 1 &&
      organizations[0].products[0].name === getProductName(Product.TextApp);

    if (isTextAppOnlySingleOrg) {
      window.location.replace(import.meta.env.VITE_APP_TA_APP_URL);
    }
  }, [organizations]);

  const fetchClientDetails = async (clientId: string): Promise<void> => {
    try {
      const result = await fetchClient(clientId, getAccessToken());
      setAppName(result.name);
    } catch (error) {
      throw new Error(`Client data couldn't be fetched: ${error}`);
    }
  };

  useEffect(() => {
    if (isFetchingProducts) {
      return;
    }

    const state = Location.getHashParam('state');
    const token = parseJwt(state);
    const originalClientId = token?.client_id;

    const shouldDisplayExternalChooser = getShouldDisplayExternalChooser(
      originalClientId,
      products
    );

    if (!shouldDisplayExternalChooser) {
      setIsOnExternalChooser(false);
    } else {
      setIsOnExternalChooser(true);
      fetchClientDetails(originalClientId);
    }
  }, [products, isFetchingProducts]);

  const hasMultipleOrganizations = organizations.length > 1;

  return (
    <Layout showHeaderLogo showDropdown>
      <div css={styles.container}>
        <AvatarHero
          avatarUrl={hasMultipleOrganizations ? null : avatarUrl}
          userName={name ?? email}
          textContent={generateAvatarHeroText({
            organizations,
            accountRoles: roles,
            hasMultipleOrganizations,
            isOnActivate,
            isOnExternalChooser,
            isAccountWhitelisted,
            appName,
          })}
        />
        {children}
      </div>
    </Layout>
  );
};
